import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { OperationGroup } from '../models/collection-vehicle.model';
import { UnitTransmissionStatusService } from 'src/app/units/services/unit-transmission-status.service';
import { UnitWithTransmissionStatus } from 'src/app/units/models/unit-transmission-status';
import { map, tap } from 'rxjs/operators';
import {
  FilterSearch,
  FilterTransmission,
} from '../models/unit-collection.model';
import { unitsToCollection } from '../helpers/convert-unitTransmission-to-collection.helper';
import { StateVehicleService } from './state-vehicle.service';
import { normalizeImei } from '../helpers/normalize-imei';
import { TransmissionStateGroup } from '../models/transmission-state-group';
import { groupVehiclesByTransmissionState } from '../adapters/group_vehicles_by_transmission_state.adapter';
import { ManagerDriversService } from './manager-drivers.service';

@Injectable({
  providedIn: 'root',
})
export class UnitCollectionService {
  private unitsCollectionPrivate$ = new BehaviorSubject<OperationGroup[]>([]);
  private unitsWithTransmissionPrivate$ = new BehaviorSubject<
    UnitWithTransmissionStatus[]
  >([]);
  private unitsCollectionOriginPrivate$ = new BehaviorSubject<OperationGroup[]>(
    []
  );
  private originalUnits: UnitWithTransmissionStatus[] = [];
  private filterTransmission: FilterTransmission = {
    isActive: false,
    data: 0,
  };
  private filterSearch: FilterSearch = {
    isActive: false,
    data: '',
  };
  private filterEye = false;

  constructor(
    unitTransmissionStatusService: UnitTransmissionStatusService,
    private stateVehicleService: StateVehicleService,
    private managerDriversService: ManagerDriversService
  ) {
    unitTransmissionStatusService.getUnitsTransmissionStatus
      .pipe(
        map((units: UnitWithTransmissionStatus[]) => {
          this.originalUnits = units;
          this.unitsCollectionOriginPrivate$.next(unitsToCollection(units));
          return this.applyFilterForTransmission(
            this.filterTransmission.data,
            this.filterTransmission.isActive
          );
        }),
        map((_: UnitWithTransmissionStatus[]) =>
          this.applyFilterForSearch(
            this.filterSearch.data,
            this.filterSearch.isActive
          )
        ),
        map((_: UnitWithTransmissionStatus[]) =>
          this.applyFilterForEye(this.filterEye)
        ),
        tap((_) => this.unitsWithTransmissionPrivate$.next(_)),
        map((units: UnitWithTransmissionStatus[]) => unitsToCollection(units)),
        tap((unitCollection: OperationGroup[]) => {
          this.unitsCollectionPrivate$.next(unitCollection);
        })
      )
      .subscribe();
  }

  get getUnitsCollection() {
    return this.unitsCollectionPrivate$.asObservable();
  }

  set setUnitsCollection(unitsCollection: OperationGroup[]) {
    this.unitsCollectionPrivate$.next(unitsCollection);
  }

  get getUnitsWithTransmission() {
    return this.unitsWithTransmissionPrivate$.asObservable();
  }

  get getUnitsCollectionOrigin() {
    return this.unitsCollectionOriginPrivate$.asObservable();
  }

  get getQuantityUnitsForTransmission(): Observable<TransmissionStateGroup[]> {
    return this.unitsWithTransmissionPrivate$.pipe(
      // solo consideramos los vehiculos originales para tener un filtro únicio
      map((_) => this.originalUnits),
      map((_) => (this.filterEye ? this.applyFilterEye(_) : _)),
      map((units) => groupVehiclesByTransmissionState(units))
    );
  }

  private applyFilterTransmission(
    units: UnitWithTransmissionStatus[],
    transmissionStatusId: number
  ): UnitWithTransmissionStatus[] {
    return units.filter((unit) => {
      return unit.transmissionStatusId === transmissionStatusId;
    });
  }

  private applyFilterSearch(
    units: UnitWithTransmissionStatus[],
    input: string
  ): UnitWithTransmissionStatus[] {
    return units.filter((unit) => {
      const driver = this.managerDriversService.getDescriptionDriverByImei(
        unit.device?.IMEI?.toString() || ''
      );
      let driverName = 'No Especificado';
      if (driver) {
        driverName = driver.long_name;
      }

      const imei = unit.device?.IMEI?.toString().toLowerCase() || '';
      const licensePlate = unit.licensePlate?.toString().toLowerCase() || '';
      const code = unit.internalCode?.toString().toLowerCase() || '';
      const unitName = unit.unitName?.toString().toLowerCase() || '';

      const driverMatches =
        driverName !== 'No Especificado' &&
        driverName.toLowerCase().includes(input);

      return (
        imei.startsWith(input) ||
        licensePlate.startsWith(input) ||
        code.includes(input) ||
        unitName.startsWith(input) ||
        driverMatches
      );
    });
  }

  private applyFilterEye(units: UnitWithTransmissionStatus[]) {
    return units.filter((unit: UnitWithTransmissionStatus) => {
      const imei = normalizeImei(unit.device?.IMEI);
      if (!imei) return false;
      const stateVehicle = this.stateVehicleService.getStateByImei(imei);
      if (!stateVehicle) return false;
      return !!stateVehicle.viewOptions?.visible;
    });
  }

  private applyFilterForTransmission(
    transmissionStatusId: number,
    isActive: boolean
  ): UnitWithTransmissionStatus[] {
    this.filterTransmission = { isActive, data: transmissionStatusId };
    const baseUnitsFilterSearch = this.filterSearch.isActive
      ? this.applyFilterSearch(this.originalUnits, this.filterSearch.data)
      : this.originalUnits;
    const baseUnitsFilterEye = this.filterEye
      ? this.applyFilterEye(baseUnitsFilterSearch)
      : baseUnitsFilterSearch;
    if (!isActive) return baseUnitsFilterEye;
    return this.applyFilterTransmission(
      baseUnitsFilterEye,
      transmissionStatusId
    );
  }

  private applyFilterForSearch(
    search: string,
    isActive: boolean
  ): UnitWithTransmissionStatus[] {
    this.filterSearch = { isActive, data: search.trim().toLowerCase() };
    const baseUnitsFilterTransmission = this.filterTransmission.isActive
      ? this.applyFilterTransmission(
          this.originalUnits,
          this.filterTransmission.data
        )
      : this.originalUnits;
    const baseUnitsFilterEye = this.filterEye
      ? this.applyFilterEye(baseUnitsFilterTransmission)
      : baseUnitsFilterTransmission;
    if (!isActive) return baseUnitsFilterEye;
    const input = search.trim().toLowerCase();
    return this.applyFilterSearch(baseUnitsFilterEye, input);
  }

  private applyFilterForEye(isActive: boolean): UnitWithTransmissionStatus[] {
    this.filterEye = isActive;
    const baseUnitsFilterTransmission = this.filterTransmission.isActive
      ? this.applyFilterTransmission(
          this.originalUnits,
          this.filterTransmission.data
        )
      : this.originalUnits;
    const baseUnitsFilterSearch = this.filterSearch.isActive
      ? this.applyFilterSearch(
          baseUnitsFilterTransmission,
          this.filterSearch.data
        )
      : baseUnitsFilterTransmission;
    if (!isActive) return baseUnitsFilterSearch;
    return this.applyFilterEye(baseUnitsFilterSearch);
  }

  public applyFilterForTransmissionPublic(
    transmissionStatusId: number,
    isActive: boolean
  ): OperationGroup[] {
    const filterUnits = this.applyFilterForTransmission(
      transmissionStatusId,
      isActive
    );
    const unitsCollection = unitsToCollection(filterUnits);
    this.unitsWithTransmissionPrivate$.next(filterUnits);
    this.unitsCollectionPrivate$.next(unitsCollection);
    return unitsCollection;
  }

  public applyFilterForSearchPublic(
    search: string,
    isActive: boolean
  ): OperationGroup[] {
    const filterUnits = this.applyFilterForSearch(search, isActive);
    const unitsCollection = unitsToCollection(filterUnits);
    this.unitsWithTransmissionPrivate$.next(filterUnits);
    this.unitsCollectionPrivate$.next(unitsCollection);
    return unitsCollection;
  }

  public applyFilterForEyePublic(isActive: boolean): OperationGroup[] {
    const filterUnits = this.applyFilterForEye(isActive);
    const unitsCollection = unitsToCollection(filterUnits);
    this.unitsWithTransmissionPrivate$.next(filterUnits);
    this.unitsCollectionPrivate$.next(unitsCollection);
    return unitsCollection;
  }

  public clearFilters() {
    this.filterTransmission = {
      isActive: false,
      data: 0,
    };
    this.filterSearch = { isActive: false, data: '' };
    const defaultUnits = this.filterEye
      ? this.applyFilterEye(this.originalUnits)
      : this.originalUnits;
    const defaultCollections = this.filterEye
      ? unitsToCollection(defaultUnits)
      : this.unitsCollectionOriginPrivate$.value;
    this.unitsCollectionPrivate$.next(defaultCollections);
    this.unitsWithTransmissionPrivate$.next(defaultUnits);
  }

  public getStateFilterEye() {
    return this.filterEye;
  }
}
